import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Loader2, Target, FileText, ShoppingBag, Send, Users, Building2, UserPlus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, PieChart, Pie, Cell, Legend } from "recharts";

const DONUT_COLORS = ["#FDD003", "#38BDF8", "#22C55E", "#F97316", "#A855F7", "#EF4444", "#14B8A6", "#FDD003", "#6366F1"];

const PERIODS = [
  { key: "today", label: "Oggi" },
  { key: "week", label: "Settimana" },
  { key: "month", label: "Mese" },
];

const eur = (v) => "€ " + Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const semColor = (s) => (s === "green" ? "#22C55E" : s === "amber" ? "#F59E0B" : "#EF4444");

function KpiBar({ label, done, target, sem }) {
  const pct = target ? Math.min(100, Math.round((done / target) * 100)) : 0;
  return (
    <div data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-foreground">{label}</span>
        <span className="text-muted-foreground"><b className="text-foreground">{done}</b> / {target}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: semColor(sem) }} />
      </div>
    </div>
  );
}

export default function SalesPerformance() {
  const { user } = useAuth();
  const isAdmin = ["admin", "partner", "team_leader"].includes(user?.role);
  const [period, setPeriod] = useState("week");
  const [sellers, setSellers] = useState([]);
  const [sellerId, setSellerId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => {
      const list = (r.data || []).filter((u) => ["sales", "qualification", "partner", "team_leader"].includes(u.role));
      setSellers(list);
    }).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    setLoading(true);
    const params = { period };
    if (isAdmin && sellerId) params.user_id = sellerId;
    api.get("/sales/performance", { params }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period, sellerId, isAdmin]);

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="sales-performance">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl">Attività</h1>
          <p className="text-sm text-muted-foreground">Azioni, obiettivi personali, lead a Outlook e ordini.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <select value={sellerId} onChange={(e) => setSellerId(e.target.value)} data-testid="seller-select"
              className="h-9 rounded-md border border-border bg-background px-3 text-sm">
              <option value="">Tutti (complessivo)</option>
              {sellers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <div className="flex rounded-md border border-border overflow-hidden">
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => setPeriod(p.key)} data-testid={`period-${p.key}`}
                className={`px-3 h-9 text-sm font-medium transition-colors ${period === p.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : !data ? (
        <Card className="p-6 text-sm text-muted-foreground">Nessun dato disponibile.</Card>
      ) : (
        <>
          <div className="text-xs text-muted-foreground">
            Periodo: {data.scope.from} → {data.scope.to} · {data.scope.business_days} giorni lavorativi
            {data.scope.is_aggregate ? " · dati complessivi" : ""}
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="p-4" data-testid="card-lead-actions">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><Users className="h-4 w-4" /> Azioni Lead</div>
              <div className="text-3xl font-bold mt-2">{data.totals.lead_actions}</div>
            </Card>
            <Card className="p-4" data-testid="card-prospect-actions">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><Building2 className="h-4 w-4" /> Azioni Prospect</div>
              <div className="text-3xl font-bold mt-2">{data.totals.prospect_actions}</div>
            </Card>
            <Card className="p-4" data-testid="card-new-prospects">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><UserPlus className="h-4 w-4" /> Nuovi Prospect</div>
              <div className="text-3xl font-bold mt-2">{data.totals.new_prospects}</div>
              <div className="text-[11px] text-muted-foreground mt-1">Inclusi nelle azioni totali</div>
            </Card>
            <Card className="p-4" data-testid="card-outlook">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><Send className="h-4 w-4" /> Lead a Outlook</div>
              <div className="text-3xl font-bold mt-2">{data.outlook_count}</div>
            </Card>
            <Card className="p-4" data-testid="card-orders">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide"><ShoppingBag className="h-4 w-4" /> Ordini</div>
              <div className="text-3xl font-bold mt-2">{data.orders.count}</div>
              <div className="text-sm text-primary font-semibold mt-1">{eur(data.orders.value)} <span className="text-muted-foreground font-normal">IVA escl.</span></div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card className="p-5 space-y-4" data-testid="azioni-compliance">
              <div className="flex items-center gap-2"><Target className="h-4 w-4 text-primary" /><h2 className="font-semibold">Compliance azioni totali</h2></div>
              <div className="flex items-end gap-3">
                <div className="text-5xl font-heading font-bold" data-testid="compliance-pct" style={{ color: semColor(data.azioni_kpi.sem) }}>{data.azioni_kpi.pct}%</div>
                <div className="text-sm text-muted-foreground mb-1"><b className="text-foreground">{data.azioni_kpi.done}</b> / {data.azioni_kpi.target} azioni</div>
              </div>
              <div className="h-3 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${data.azioni_kpi.pct}%`, backgroundColor: semColor(data.azioni_kpi.sem) }} />
              </div>
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="rounded-lg border border-border p-3" data-testid="split-lead">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Users className="h-3.5 w-3.5" /> Lead</div>
                  <div className="text-2xl font-bold mt-1">{data.by_entity.lead}</div>
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="split-prospect">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground"><Building2 className="h-3.5 w-3.5" /> Prospect</div>
                  <div className="text-2xl font-bold mt-1">{data.by_entity.prospect}</div>
                </div>
              </div>
            </Card>

            <Card className="p-5" data-testid="by-type-donut">
              <div className="flex items-center gap-2 mb-2"><FileText className="h-4 w-4 text-primary" /><h2 className="font-semibold">Distribuzione per tipo</h2></div>
              {(data.by_type || []).length === 0 ? (
                <div className="text-sm text-muted-foreground py-10 text-center">Nessuna azione nel periodo.</div>
              ) : (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={data.by_type} dataKey="count" nameKey="label" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {data.by_type.map((e, i) => <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                      </Pie>
                      <ReTooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Card>
          </div>

          <Card className="p-5" data-testid="daily-breakdown">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="h-4 w-4 text-primary" /><h2 className="font-semibold">Azioni giornaliere</h2>
              <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: "#FDD003" }} /> Lead</span>
                <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: "#38BDF8" }} /> Prospect</span>
              </span>
            </div>
            {data.daily.length === 0 ? (
              <div className="text-sm text-muted-foreground">Nessuna azione registrata nel periodo.</div>
            ) : (
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={data.daily} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9aa0a6" }} tickFormatter={(d) => (d || "").slice(5)} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#9aa0a6" }} />
                    <ReTooltip contentStyle={{ background: "#111", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#fff" }} />
                    <Bar dataKey="lead" name="Lead" fill="#FDD003" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="prospect" name="Prospect" fill="#38BDF8" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          {(data.leaderboard || []).length > 0 && (
            <Card className="p-5" data-testid="leaderboard">
              <div className="flex items-center gap-2 mb-3"><ShoppingBag className="h-4 w-4 text-primary" /><h2 className="font-semibold">Classifica venditori · ordini e valore</h2><span className="text-xs text-muted-foreground">(IVA escl.)</span></div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground border-b border-border">
                      <th className="text-left font-semibold py-2 pr-2">#</th>
                      <th className="text-left font-semibold py-2 pr-2">Venditore</th>
                      <th className="text-right font-semibold py-2 px-2">Ordini</th>
                      <th className="text-right font-semibold py-2 px-2">Valore</th>
                      <th className="text-right font-semibold py-2 px-2">Az. Lead</th>
                      <th className="text-right font-semibold py-2 px-2">Az. Prospect</th>
                      <th className="text-right font-semibold py-2 pl-2">Nuovi Prosp.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.leaderboard.map((r, i) => (
                      <tr key={r.user_id} className="border-b border-border/50" data-testid={`lb-row-${r.user_id}`}>
                        <td className="py-2 pr-2 text-muted-foreground">{i + 1}</td>
                        <td className="py-2 pr-2 font-medium">{r.name}</td>
                        <td className="py-2 px-2 text-right">{r.orders}</td>
                        <td className="py-2 px-2 text-right font-semibold text-primary">{eur(r.value)}</td>
                        <td className="py-2 px-2 text-right">{r.lead_actions}</td>
                        <td className="py-2 px-2 text-right">{r.prospect_actions}</td>
                        <td className="py-2 pl-2 text-right">{r.new_prospects}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
